import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BatchConstants } from 'lookup/BatchConstants';
import { CycleConstants } from 'lookup/CycleConstants';

import { Container, Row, Col, Button } from 'react-bootstrap/';
import { MdClose } from "react-icons/md";
import ResetListState from '../../../components/ResetListState';

import { formatDateWithTime } from 'services/MomentUtils';
import { twoDecimalPlaceRounder } from 'services/UtilityService';

import { updateCycleList, updateSwitchList, updateCycleListState, updateSwitchListState, resetError } from "./CycleList.actions";

import './CycleList.css';
import { WrapTextAndCenterAlign } from 'components/WrapText';
import ServerSideReactTable from 'commons/table/ServerSideReactTable';
import { FilterTypes, ColumnTypes } from 'commons/table/Constants';
import ClientSideReactTable from 'commons/table/ClientSideReactTable';
import { WrapTextAndJustifyCenter } from 'components/WrapText';
import EditCycle from './EditCycle';
import ReactBadge from "react-bootstrap/Badge";
const Badge = (props) => (
  <div>
    <ReactBadge {...props}>{props.children}</ReactBadge>
  </div>
);


const DEFAULT_PAGE_SIZE = 15;
const SELECTED_ROW_BG_COLOR = "#42a5f5";
const SELECTED_ROW_TEXT_COLOR = "#ffffff";

const CycleList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    isCycleListLoading,
    cycleListData,
    totalCycleElements,
    cycleListPages,
    cycleListState: initialCycleListState,
    isSwitchListLoading,
    switchListData,
    totalSwitchElements,
    switchListPages,
    switchListState: initialSwitchListState,
    selectedCycleId: reduxSelectedCycleId,
  } = useSelector(state => ({
    isCycleListLoading: state.cycleList.isCycleListLoading,
    cycleListData: state.cycleList.cycleListData ? state.cycleList.cycleListData : [],
    totalCycleElements: state.cycleList.isCycleListLoading ? "Loading..." : (state.cycleList.totalCycleElements ? state.cycleList.totalCycleElements : 0),
    cycleListPages: state.cycleList.cycleListPages ? state.cycleList.cycleListPages : 0,
    cycleListState: state.cycleList.cycleListState ? state.cycleList.cycleListState : {
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      sorted: BatchConstants.DEFAULT_BATCH_LIST_SORTING,
      filtered: [],
      resized: [],
      expanded: {}
    },
    isSwitchListLoading: state.cycleList.isSwitchListLoading,
    switchListData: state.cycleList.switchListData ? state.cycleList.switchListData : [],
    totalSwitchElements: state.cycleList.totalSwitchElements ? state.cycleList.totalSwitchElements : "Loading...",
    switchListPages: state.cycleList.switchListPages ? state.cycleList.switchListPages : 0,
    switchListState: state.cycleList.switchListState ? state.cycleList.switchListState : {
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      sorted: [],
      filtered: [],
      resized: [],
      expanded: {}
    },
    selectedCycleId: state.cycleList.selectedCycleId ? state.cycleList.selectedCycleId : -1,
  }));

  const [cycleListState, setCycleListState] = useState(initialCycleListState);
  const [switchListState, setSwitchListState] = useState(initialSwitchListState);
  const [selectedCycleId, setSelectedCycleId] = useState(reduxSelectedCycleId);
  const [displayAllColumn, setDisplayAllColumn] = useState(true);

  // Track last dispatched selectedCycleId to prevent duplicate API calls
  const lastDispatchedCycleIdRef = useRef(null);
  // Store switchListState in a ref to avoid dependency issues
  const switchListStateRef = useRef(switchListState);
  // Store selectedCycleId in a ref to avoid dependency issues in sync effect
  const selectedCycleIdRef = useRef(selectedCycleId);
  // Track previous Redux state to prevent unnecessary syncs
  const prevCycleListStateRef = useRef(initialCycleListState);
  const prevSwitchListStateRef = useRef(initialSwitchListState);
  // Track previous Redux selectedCycleId to prevent sync loop
  const prevReduxSelectedCycleIdRef = useRef(reduxSelectedCycleId);
  // Flag to prevent sync effect from running when we just dispatched updateSwitchListState
  const justDispatchedSwitchListStateRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    switchListStateRef.current = switchListState;
  }, [switchListState]);

  useEffect(() => {
    selectedCycleIdRef.current = selectedCycleId;
  }, [selectedCycleId]);

  // Sync local cycleListState with Redux state when it changes (e.g., after reset)
  useEffect(() => {
    const prevStr = JSON.stringify(prevCycleListStateRef.current);
    const currentStr = JSON.stringify(initialCycleListState);
    if (prevStr !== currentStr) {
      setCycleListState(initialCycleListState);
      prevCycleListStateRef.current = initialCycleListState;
    }
  }, [initialCycleListState]);

  useEffect(() => {
    if (justDispatchedSwitchListStateRef.current) {
      justDispatchedSwitchListStateRef.current = false;
      prevSwitchListStateRef.current = initialSwitchListState;
      return;
    }
    const prevStr = JSON.stringify(prevSwitchListStateRef.current);
    const currentStr = JSON.stringify(initialSwitchListState);
    if (prevStr !== currentStr) {
      setSwitchListState(initialSwitchListState);
      prevSwitchListStateRef.current = initialSwitchListState;
    }
  }, [initialSwitchListState]);

  const updateData = useCallback((state, preventUpdate) => {
    const filteredForStatus = state.filtered.filter(element => !(element.id === 'status' && element.value.includes("ALL")))
    if (filteredForStatus.length !== state.filtered.length) {
      state.filtered = filteredForStatus
    }
    if (!preventUpdate) {
      if (state.sorted !== BatchConstants.DEFAULT_BATCH_LIST_SORTING)
        state.sorted.forEach((element) => { element.isDefault = false });
      dispatch(updateCycleList(state.pageSize, state.page, state.sorted, state.filtered, state.resized, state.expanded));
    } else {
      setCycleListState(state);
    }
  }, [dispatch]);

  useEffect(() => {
    updateData(cycleListState);
  }, []);

  useEffect(() => {
    if (reduxSelectedCycleId !== prevReduxSelectedCycleIdRef.current) {
      const currentSelectedCycleId = selectedCycleIdRef.current;
      if (reduxSelectedCycleId !== -1 || currentSelectedCycleId === -1) {
        if (reduxSelectedCycleId !== currentSelectedCycleId) {
      setSelectedCycleId(reduxSelectedCycleId);
        }
      }
      prevReduxSelectedCycleIdRef.current = reduxSelectedCycleId;
    }
  }, [reduxSelectedCycleId]);

  const handler = useCallback(() => {
    updateData(cycleListState, false);
  }, [cycleListState, updateData]);

  const renderActions = useCallback((rowData) => {
    return (
      <span className="action-button">
        <EditCycle
          cycle={rowData}
          showButton={true}
          handler={handler}
        ></EditCycle>
      </span>
    );
  }, [handler]);

  const renderRowDescription = useCallback((rowData) => {
    return (
      <span style={{ display: "flex" }}>
        <span style={{ flex: "9" }}>
          {rowData.description}
        </span>

        <span className="edited-tag">
          {rowData.descriptionEdited ?
            <Badge
              variant="primary"
              box="true"
              className="badge-custom-edited"
            >
              Edited
            </Badge>
            : ""}
        </span>
      </span>
    );
  }, []);

  const renderDateCell = useCallback((date) => {
    return (
      <span style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        {formatDateWithTime(date)}
      </span>
    );
  }, []);

  const renderSavingsCell = useCallback((savings) => `$ ${twoDecimalPlaceRounder(savings)}`, []);

  const handleRowClick = (rowInfo) => {
    setDisplayAllColumn(false);
    setSelectedCycleId(rowInfo.original.wfCycleId);
  };

  // Fetch switch list when a cycle is selected (initial load)
  // Only dispatch when selectedCycleId changes, using current switchListState values
  useEffect(() => {
    if (selectedCycleId !== -1 && selectedCycleId !== lastDispatchedCycleIdRef.current) {
      // Use ref to get current switchListState values without adding to dependencies
      const currentState = switchListStateRef.current;
      dispatch(updateSwitchList(selectedCycleId, currentState.pageSize, currentState.page, currentState.sorted, currentState.filtered, currentState.resized, currentState.expanded));
      lastDispatchedCycleIdRef.current = selectedCycleId;
    }
  }, [selectedCycleId, dispatch]);

  const onUpdateTable = useCallback((filterValues, sortBy, pageSize, pageNo) => {
    const newState = {
      ...cycleListState,
      pageSize: pageSize,
      page: pageNo,
      sorted: sortBy ? sortBy : [],
      filtered: filterValues,
    };
    setCycleListState(newState);
    updateData(newState);
  }, [cycleListState, updateData]);

  const onUpdateSwitchTable = useCallback((filterValues, sortBy, pageSize, pageNo) => {
    const currentState = switchListStateRef.current;
    const newState = {
      ...currentState,
      pageSize: pageSize,
      page: pageNo,
      sorted: sortBy ? sortBy : [],
      filtered: filterValues,
    };
    setSwitchListState(newState);
    // Dispatch immediately when user changes pagination/filtering (matches original behavior)
    if (selectedCycleId !== -1) {
      justDispatchedSwitchListStateRef.current = true;
      dispatch(updateSwitchList(selectedCycleId, newState.pageSize, newState.page, newState.sorted, newState.filtered, newState.resized, newState.expanded));
      dispatch(updateSwitchListState(newState));
    }
  }, [selectedCycleId, dispatch]);

  const widthOfTotalSwitch = displayAllColumn ? 95 : 105;
  const widthOfSwitchDescription = displayAllColumn ? 600 : 325;

  const columns = [
      {
        header: "Cycle Description",
        accessor: "description",
        accessorKey: "description",
        width: widthOfSwitchDescription,
        enableSorting: false,
        enableColumnFilter: true,
        columnType: ColumnTypes.Custom,
        filterType: FilterTypes.SearchableInput,
        customComponent: (value, row) => {
          return renderRowDescription(row.original);
        },
      },
      {
        header: "Cycle Status",
        accessor: "status",
        accessorKey: "status",
        width: 120,
        show: displayAllColumn,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Custom,
        filterType: FilterTypes.SingleSelect,
        filterOptions: CycleConstants.statuses,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Scheduled Date",
        accessor: "dateScheduled",
        accessorKey: "dateScheduled",
        width: 170,
        enableSorting: true,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return renderDateCell(value)
        },
      },
      {
        header: "Total Switches",
        accessor: "totalSwitches",
        accessorKey: "totalSwitches",
        show: displayAllColumn,
        width: widthOfTotalSwitch,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Projected Savings",
        accessor: "projectedSavings",
        accessorKey: "projectedSavings",
        width: 140,
        show: displayAllColumn,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{renderSavingsCell(value)}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Completed Date/Time",
        accessor: "completionDate",
        accessorKey: "completionDate",
        enableSorting: false,
        width: 210,
        show: displayAllColumn,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) => {
          return row.original.status === "CLOSED" ? renderDateCell(value) : <WrapTextAndJustifyCenter>N/A</WrapTextAndJustifyCenter>
        },
      },
      {
        header: "Action",
        accessor: "action",
        accessorKey: "action",
        enableSorting: false,
        enableFiltering: false,
        show: displayAllColumn,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) => {
          return renderActions(row.original);
        },
      },
    ];

    // Process switchListData to add wfBatchId, memoized to prevent mutation on every render
    const processedSwitchListData = useMemo(() => {
      return switchListData.map(element => ({
        ...element,
        wfBatchId: element.wfBatchId || '-'
      }));
    }, [switchListData]);
    
    const switchColumns = [
      {
        header: "Switch Id",
        accessor: "switchId",
        accessorKey: "switchId",
        width: 140,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Cust",
        accessor: "customerNameAbbreviation",
        accessorKey: "customerNameAbbreviation",
        width: 60,
        show: true,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Client",
        accessor: "clientNameAbbreviation",
        accessorKey: "clientNameAbbreviation",
        width: 60,
        show: true,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Switch Id",
        accessor: "switchNumber",
        accessorKey: "switchNumber",
        width: 60,
        show: true,
        enableSorting: true,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Switch Status",
        accessor: "currentStatus",
        accessorKey: "currentStatus",
        width: 260,
        show: true,
        enableSorting: true,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Scheduled Date",
        accessor: "scheduledDispatchTime",
        accessorKey: "scheduledDispatchTime",
        width: 150,
        show: true,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value ? renderDateCell(value) : 'N/A'}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Fax Status",
        accessor: "faxBucketStatus",
        accessorKey: "faxBucketStatus",
        width: 150,
        show: true,
        enableSorting: true,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{value ? value : "N/A"}</WrapTextAndCenterAlign>
        },
      },
      {
        header: "Projected Savings",
        accessor: "projectedSavings",
        accessorKey: "projectedSavings",
        width: 140,
        show: true,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value) => {
          return <WrapTextAndCenterAlign>{renderSavingsCell(value)}</WrapTextAndCenterAlign>
        },
      },
    ];

    return (
      <Container className="list-container" fluid>
        <Row >
          <Col md={12}>
            <div className='codex-version-display-bar'>
              <span style={{ float: 'left' }} >   Total Cycles: <strong>{totalCycleElements}</strong></span>
              <strong style={{ float: 'center' }}>Cycle List</strong>
              {selectedCycleId === -1 ?
                <ResetListState
                  onReset={() => updateData({
                    ...cycleListState, displayAllColumn: true, sorted: BatchConstants.DEFAULT_BATCH_LIST_SORTING
                    , filtered: []
                  })}
                  resetterDisabled={cycleListState.sorted === BatchConstants.DEFAULT_BATCH_LIST_SORTING &&
                    cycleListState.filtered.length === 0}
                /> :
                null
              }
            </div>
          </Col>
        </Row>
        <Row className="list-container" test-id="cycle-list-test-id">
          <Col md={selectedCycleId === -1 ? 12 : 6}>
            <ServerSideReactTable
              columnDefination={columns}
              data={cycleListData}
              currentPage={cycleListState.page}
              sorted={cycleListState.sorted}
              filters={cycleListState.filtered}
              totalPages={cycleListPages}
              totalRecords={totalCycleElements == "loading" ? 0 : totalCycleElements}
              loading={isCycleListLoading}
              enableMultiRowSelection={false}
              addRowSelection={false}
              defaultPageSize={DEFAULT_PAGE_SIZE}
              pageSizeOptions={[5, 10, 15, 20, 30, 50]}
              onUpdateTableData={onUpdateTable}
              onRowClick={(rowInfo) => {
                if (rowInfo !== undefined && rowInfo.column.id != 'action') {
                  handleRowClick(rowInfo);
                }
              }}
              trStyle={(rowInfo) => {
                return (
                  {
                    background: (rowInfo && rowInfo.original.wfCycleId === selectedCycleId) ? SELECTED_ROW_BG_COLOR : "",
                    color: (rowInfo && rowInfo.original.wfCycleId === selectedCycleId) ? SELECTED_ROW_TEXT_COLOR : ""
                  })
              }
              }
            />
          </Col>
          {
            selectedCycleId !== -1 ?
              <Col md={6} className="tiny-top-spacer" test-id="cycle-items-list-test-id">
                <Row >
                  <Col md={12}>
                    <Button className={"pull-right"} size="sm" variant="custom-hollow" onClick={() => { setSelectedCycleId(-1); setDisplayAllColumn(true); }} > <MdClose /> </Button>
                    <h4 className="pull-left">
                      Switches for Cycle
                    </h4>
                  </Col>
                </Row>
                {
                  <Row className="tiny-top-spacer list-container">
                    <Col md={12}>
                      <ClientSideReactTable
                        columnDefination={switchColumns}
                        data={processedSwitchListData}
                        loading={isSwitchListLoading}
                        overrideRowId={'switchId'}
                        defaultPageSize={DEFAULT_PAGE_SIZE}
                        pageSizeOptions={[5, 10, 15, 20, 30, 50]}
                        onUpdateTableData={onUpdateSwitchTable}
                        onRowClick={(rowInfo) => {
                          if (rowInfo !== undefined) {
                            navigate(`/switch/${rowInfo.original.wfItemId}`);
                          }
                        }}
                      />
                    </Col>
                  </Row>

                }
              </Col>
              : ""
          }
        </Row>


      </Container>
    );
}

export default CycleList;